import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FilterOption, ScorecardSelection } from './scorecardFilters';

const useStyles = makeStyles({
  fullWidth: { width: '100%' },
  boxLabel: {
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
});

const FixedWidthFormControlLabel = withStyles(_theme => ({
  label: {
    width: '100%',
  },
  root: {
    width: '90%',
  },
}))(FormControlLabel);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function RenderOptionLabel(props: {
  option: FilterOption;
  isSelected: boolean;
}) {
  const classes = useStyles();
  const { option, isSelected } = props;
  return (
    <Box className={classes.fullWidth}>
      <FixedWidthFormControlLabel
        className={classes.fullWidth}
        control={
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={isSelected}
          />
        }
        onClick={event => event.preventDefault()}
        label={
          <Tooltip title={option.value}>
            <Box display="flex" alignItems="center">
              <Box className={classes.boxLabel}>
                <Typography noWrap>{option.label}</Typography>
              </Box>
            </Box>
          </Tooltip>
        }
      />
    </Box>
  );
}

/**
 * One dropdown. Every filter on this page is the same shape: pick none to leave
 * that filter out, or pick as many values as you want.
 *
 * The placeholder carries the "none selected" meaning, since an empty box on its
 * own reads as broken rather than as "everything".
 */
function MultiSelectFilter(props: {
  label: string;
  placeholder: string;
  options: FilterOption[];
  selected: string[];
  loading?: boolean;
  onChange: (values: string[]) => void;
}) {
  const { label, placeholder, options, selected, loading, onChange } = props;
  const value = options.filter(option => selected.includes(option.value));

  return (
    <Box pb={1} pt={1}>
      <Typography variant="button" component="label">
        {label}
        <Autocomplete
          multiple
          disableCloseOnSelect
          disableClearable={false}
          options={options}
          value={value}
          loading={loading}
          limitTags={2}
          getOptionLabel={option => option.label}
          getOptionSelected={(option, selectedOption) =>
            option.value === selectedOption.value
          }
          onChange={(_: object, changed) => {
            onChange(changed.map(option => option.value));
          }}
          renderOption={(option, { selected: isSelected }) => (
            <RenderOptionLabel option={option} isSelected={isSelected} />
          )}
          size="small"
          popupIcon={<ExpandMoreIcon />}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={value.length === 0 ? placeholder : undefined}
            />
          )}
        />
      </Typography>
    </Box>
  );
}

export type FiltersProps = {
  options: {
    statuses: FilterOption[];
    owners: FilterOption[];
    kinds: FilterOption[];
    entities: FilterOption[];
    failingChecks: FilterOption[];
    checks: FilterOption[];
  };
  selection: ScorecardSelection;
  selectionChanged: (selection: ScorecardSelection) => void;
  loading?: boolean;
};

/**
 * The scorecards page sidebar.
 *
 * Every filter narrows and none of them contradict each other, which is what the
 * upstream pair of Yes/No dropdowns could not manage. Options come from the rows
 * on the page rather than from a fixed list, so the sidebar never offers a value
 * that matches nothing.
 */
export const Filters = (props: FiltersProps) => {
  const { options, selection, selectionChanged, loading } = props;

  const update = (changed: Partial<ScorecardSelection>) =>
    selectionChanged({ ...selection, ...changed });

  return (
    <>
      <MultiSelectFilter
        label="Status"
        placeholder="Any status"
        options={options.statuses}
        selected={selection.statuses}
        onChange={statuses => update({ statuses })}
      />
      <MultiSelectFilter
        label="Owner"
        placeholder="Any owner"
        options={options.owners}
        selected={selection.owners}
        loading={loading}
        onChange={owners => update({ owners })}
      />
      <MultiSelectFilter
        label="Kind"
        placeholder="Any kind"
        options={options.kinds}
        selected={selection.kinds}
        loading={loading}
        onChange={kinds => update({ kinds })}
      />
      <MultiSelectFilter
        label="Entity"
        placeholder="Any entity"
        options={options.entities}
        selected={selection.entityRefs}
        loading={loading}
        onChange={entityRefs => update({ entityRefs })}
      />
      <MultiSelectFilter
        label="Failing check"
        placeholder="Any failing check"
        options={options.failingChecks}
        selected={selection.failingCheckIds}
        loading={loading}
        onChange={failingCheckIds => update({ failingCheckIds })}
      />
      <MultiSelectFilter
        label="Checks to run"
        placeholder="All checks"
        options={options.checks}
        selected={selection.checkIds}
        loading={loading}
        onChange={checkIds => update({ checkIds })}
      />
    </>
  );
};
